import { createBrandGraphic } from "@groundspark/brand";
import {
  authenticationForm,
  roleLabels,
  type BrowserAuthManager,
} from "@groundspark/browser-auth";
import { CanvassApi } from "./api";
import { CanvassingAccess } from "./access";
import {
  dateLabel,
  isQrSecret,
  type Invitation,
  type MembershipPass,
  type IssuedInvitation,
  type Notification,
} from "./domain";
import { qrImage, QrScanner } from "./qr";
import {
  actions,
  button,
  countdown,
  element,
  errorMessage,
  field,
  heading,
  Lifetime,
  message,
  notice,
  panel,
  runButton,
  selectField,
  type View,
} from "./ui";

export class AuthenticationView implements View {
  public readonly element = element("main", "auth-screen");
  private readonly lifetime = new Lifetime();
  private scanner: QrScanner | null = null;
  public constructor(
    private readonly auth: BrowserAuthManager,
    private readonly api: CanvassApi,
    private readonly signedIn: () => Promise<void>,
    error?: string,
    fragment?: string,
  ) {
    this.element.id = "main-content";
    this.element.tabIndex = -1;
    const parameters = new URLSearchParams((fragment ?? "").replace(/^#/, ""));
    const action = parameters.get("action");
    const token = parameters.get("token");
    if (action && token && isQrSecret(token)) {
      if (action === "invite") void this.inspect(token);
      else if (action === "verify" || action === "change" || action === "reset")
        this.confirmEmail(action, token);
      else this.signIn("This account link is invalid.");
    } else this.signIn(error);
  }
  private card(title: string, ...children: (Node | string)[]): HTMLElement {
    const card = element(
      "section",
      "auth-card",
      createBrandGraphic(document, { tone: "light" }),
      heading(1, title),
      ...children,
    );
    this.element.replaceChildren(card);
    return card;
  }
  private signIn(error?: string): void {
    this.scanner?.dispose();
    this.scanner = null;
    this.card(
      "Canvassing",
      element("p", "auth-card__subtitle", "Campaign intelligence. On your doorstep."),
      authenticationForm(
        this.auth,
        async () => {
          if (this.lifetime.active) await this.signedIn();
        },
        error,
      ),
      button("Scan QR Code", "button button--white", () => this.scan()),
    );
  }
  private scan(): void {
    this.auth.cancelSignIn();
    const scanner = new QrScanner(isQrSecret, (code) => {
      scanner.dispose();
      this.scanner = null;
      void this.inspect(code);
    });
    this.scanner = scanner;
    this.card(
      "Scan invitation",
      scanner.element,
      button("Back to sign in", "button button--white", () => this.signIn()),
    );
  }
  private async inspect(code: string): Promise<void> {
    const error = errorMessage();
    const card = this.card("Your invitation", notice("Checking invitation…"));
    try {
      const invitation = await this.api.inspectInvitation(code, this.lifetime.abort.signal);
      if (!this.lifetime.active || this.element.firstElementChild !== card) return;
      const details = panel(
        heading(2, invitation.organization.name),
        element("p", null, `Branch: ${invitation.branch.name}`),
        element("p", "muted", `Invitation expires ${dateLabel(invitation.expires_at)}.`),
      );
      card.replaceChildren(
        createBrandGraphic(document, { tone: "light" }),
        heading(1, "You are invited to join"),
        details,
        button("Continue", "button button--white", () => this.signup(code, invitation)),
        button("Back to sign in", "button button--white", () => this.signIn()),
      );
    } catch (failure) {
      if (this.lifetime.active && this.element.firstElementChild === card)
        card.append(
          error,
          button("Back to sign in", "button button--white", () => this.signIn()),
        );
      error.textContent = message(failure);
    }
  }
  private signup(code: string, invitation: Invitation): void {
    const first = field("First name", "first_name", "text", "given-name");
    const last = field("Last name", "last_name", "text", "family-name");
    const phone = field("Phone number", "phone_number", "tel", "tel");
    phone.input.maxLength = 64;
    const email =
      invitation.kind === "organiser_qr" ? field("Email", "email", "email", "email") : null;
    const password = field(
      "Password (at least 15 characters)",
      "password",
      "password",
      "new-password",
    );
    password.input.minLength = 15;
    const confirmation = field("Confirm password", "confirmation", "password", "new-password");
    const error = errorMessage();
    const submit = element("button", "button button--white", "Create account");
    submit.type = "submit";
    const form = element(
      "form",
      "authentication-form",
      first.label,
      last.label,
      phone.label,
      email?.label,
      password.label,
      confirmation.label,
      error,
      submit,
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (submit.disabled || !form.reportValidity()) return;
      if (confirmation.input.value !== password.input.value) {
        error.textContent = "The passwords do not match.";
        return;
      }
      if (
        ![first.input, last.input, phone.input].every((input) => input.value.trim().length > 0)
      ) {
        error.textContent = "Enter your name and phone number.";
        return;
      }
      submit.disabled = true;
      void this.api
        .submitSignup({
          invitation_code: code,
          first_name: first.input.value.trim(),
          last_name: last.input.value.trim(),
          phone_number: phone.input.value.trim(),
          password: password.input.value,
          ...(email ? { email: email.input.value.trim() } : {}),
        })
        .then(() => {
          if (this.lifetime.active && this.element.firstElementChild === card)
            this.card(
              "Check your email",
              notice(
                "Your account is created. Verify your email address, then sign in. Signing in can request another verification email.",
              ),
              button("Back to sign in", "button button--white", () => this.signIn()),
            );
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        })
        .finally(() => {
          password.input.value = confirmation.input.value = "";
          submit.disabled = false;
        });
    });
    const card = this.card(
      "Create your account",
      element("p", null, `${invitation.organization.name} · ${invitation.branch.name}`),
      form,
      button("Back", "button button--white", () => void this.inspect(code)),
    );
  }
  private confirmEmail(action: "verify" | "change" | "reset", token: string): void {
    const title =
      action === "reset"
        ? "Reset your password"
        : action === "change"
          ? "Confirm your new email"
          : "Verify your email";
    const password = field(
      action === "reset" ? "New password (at least 15 characters)" : "Current password",
      "password",
      "password",
      action === "reset" ? "new-password" : "current-password",
    );
    if (action === "reset") password.input.minLength = 15;
    const confirmation =
      action === "reset"
        ? field("Confirm password", "confirmation", "password", "new-password")
        : null;
    const error = errorMessage();
    const submit = element("button", "button button--white", title);
    submit.type = "submit";
    const form = element(
      "form",
      "authentication-form",
      password.label,
      confirmation?.label,
      error,
      submit,
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!form.reportValidity() || submit.disabled) return;
      if (confirmation && confirmation.input.value !== password.input.value) {
        error.textContent = "The passwords do not match.";
        return;
      }
      submit.disabled = true;
      void this.api
        .confirmEmail(action, token, password.input.value)
        .then(() => {
          if (this.lifetime.active && this.element.firstElementChild === card)
            this.card(
              "All done",
              notice(
                action === "reset"
                  ? "Your password has changed. Sign in with your new password."
                  : "Your email address is verified. You can now sign in.",
              ),
              button("Sign in", "button button--white", () => this.signIn()),
            );
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        })
        .finally(() => {
          password.input.value = "";
          if (confirmation) confirmation.input.value = "";
          submit.disabled = false;
        });
    });
    const card = this.card(
      title,
      form,
      button("Back to sign in", "button button--white", () => this.signIn()),
    );
  }
  public dispose(): void {
    this.lifetime.dispose();
    this.scanner?.dispose();
  }
}

export class HomeView implements View {
  public readonly element = element("div", "page");
  private readonly lifetime = new Lifetime();
  public constructor(access: CanvassingAccess, goCanvass: () => void, goSessions: () => void) {
    const error = errorMessage();
    const totals = element("div", "totals", notice("Loading your totals…"));
    const refresh = async () => {
      error.textContent = "";
      try {
        const result = await access.api.progress(this.lifetime.abort.signal);
        if (!this.lifetime.active) return;
        totals.replaceChildren(
          panel(
            element("strong", "total", result.elector_count.toLocaleString("en-GB")),
            heading(2, "Voters canvassed"),
          ),
          panel(
            element("strong", "total", result.street_count.toLocaleString("en-GB")),
            heading(2, "Streets leafletted"),
          ),
        );
      } catch (failure) {
        if (this.lifetime.active) error.textContent = message(failure);
      }
    };
    this.element.append(
      element("p", "eyebrow", access.account.organization.name),
      heading(1, `Hello, ${access.account.user.displayName}`),
      element("p", "lede", "Every conversation counts."),
      totals,
      error,
      actions(
        button("Go canvassing", "button", goCanvass),
        button("Canvassing sessions", "button button--secondary", goSessions),
        runButton("Refresh totals", refresh, error),
      ),
      element("p", "muted", "Your personal activity across all projects in this organisation."),
    );
    void refresh();
  }
  public dispose(): void {
    this.lifetime.dispose();
  }
}

function passContent(pass: MembershipPass | IssuedInvitation, label: string): HTMLElement {
  const box = element(
    "div",
    "qr-pass",
    qrImage(pass.code, label),
    element("p", "qr-pass__expired", "This code has expired. Generate a new code."),
    element("p", null, "Expires in ", countdown(pass.expires_at)),
  );
  box.dataset.expiry = pass.expires_at;
  return box;
}

export class MembershipView implements View {
  public readonly element = element("div", "page page--narrow");
  private readonly lifetime = new Lifetime();
  private generation = 0;
  public constructor(api: CanvassApi) {
    const error = errorMessage();
    const content = element("div");
    const create = async () => {
      const generation = ++this.generation;
      content.replaceChildren(notice("Creating your membership code…"));
      try {
        const pass = await api.membershipPass(this.lifetime.abort.signal);
        if (this.lifetime.active && generation === this.generation)
          content.replaceChildren(passContent(pass, "Your single-use membership QR code"));
      } catch (failure) {
        if (this.lifetime.active && generation === this.generation) {
          content.replaceChildren();
          error.textContent = message(failure);
        }
      }
    };
    const next = runButton("New code", create, error);
    this.element.append(
      heading(1, "Membership"),
      element("p", "lede", "Show this code to an organiser to verify your membership."),
      panel(content, error, next),
      notice(
        "Each code works once and lasts five minutes. A new code replaces the previous one immediately.",
      ),
    );
    next.click();
  }
  public dispose(): void {
    this.lifetime.dispose();
  }
}

export class AdminView implements View {
  public readonly element = element("div", "page page--narrow");
  private readonly lifetime = new Lifetime();
  private scanner: QrScanner | null = null;
  private toolGeneration = 0;
  public constructor(private readonly api: CanvassApi) {
    this.menu();
  }
  private menu(): void {
    this.toolGeneration += 1;
    this.scanner?.dispose();
    this.scanner = null;
    this.element.replaceChildren(
      heading(1, "Admin"),
      element(
        "div",
        "tool-grid",
        button("Verify memberships", "tool-button", () => this.verify()),
        button("Invite new members", "tool-button", () => this.invite()),
      ),
    );
  }
  private tool(title: string): HTMLElement {
    this.scanner?.dispose();
    this.scanner = null;
    const content = panel();
    this.element.replaceChildren(
      button("← Back to Admin", "button button--text", () => this.menu()),
      heading(1, title),
      content,
    );
    return content;
  }
  private verify(): void {
    const generation = ++this.toolGeneration;
    const content = this.tool("Verify memberships");
    const scan = () => {
      this.scanner?.dispose();
      const scanner = new QrScanner(
        (code) => /^groundspark:membership:[A-Za-z0-9_-]{43}$/.test(code),
        (code) => {
          scanner.dispose();
          this.scanner = null;
          content.replaceChildren(notice("Verifying with Groundspark…"));
          void this.api
            .verifyMembership(code, this.lifetime.abort.signal)
            .then((verdict) => {
              if (!this.lifetime.active || generation !== this.toolGeneration) return;
              content.replaceChildren(
                element(
                  "div",
                  verdict.valid ? "verdict verdict--valid" : "verdict verdict--invalid",
                  verdict.valid ? "✓" : "×",
                ),
                heading(
                  2,
                  verdict.valid ? `${verdict.first_name} ${verdict.last_name}` : "Invalid code",
                ),
                element(
                  "p",
                  null,
                  verdict.valid
                    ? verdict.branch.name
                    : "This code is invalid, expired or already used. Ask for a new code.",
                ),
                button("Scan next", "button", scan),
              );
            })
            .catch((failure: unknown) => {
              if (!this.lifetime.active || generation !== this.toolGeneration) return;
              content.replaceChildren(
                heading(2, "Couldn’t verify"),
                element("p", "error", message(failure)),
                element(
                  "p",
                  null,
                  "A connection failure does not establish that this person is not a member. Ask them for a new code before retrying.",
                ),
                button("Scan next", "button", scan),
              );
            });
        },
      );
      this.scanner = scanner;
      content.replaceChildren(scanner.element);
    };
    scan();
  }
  private invite(): void {
    const generation = ++this.toolGeneration;
    const content = this.tool("Invite new members");
    const error = errorMessage();
    const code = element("div");
    const create = async () => {
      code.replaceChildren(notice("Creating invitation…"));
      try {
        const invitation = await this.api.invite(this.lifetime.abort.signal);
        if (this.lifetime.active && generation === this.toolGeneration)
          code.replaceChildren(
            heading(2, invitation.organization.name),
            element("p", null, `Branch: ${invitation.branch.name}`),
            passContent(invitation, "Single-use Groundspark signup invitation"),
          );
      } catch (failure) {
        if (this.lifetime.active && generation === this.toolGeneration) {
          code.replaceChildren();
          error.textContent = message(failure);
        }
      }
    };
    const next = runButton("New code", create, error);
    content.append(
      code,
      error,
      next,
      notice(
        "Creating a new code immediately invalidates your previous pending QR invitations.",
      ),
    );
    next.click();
  }
  public dispose(): void {
    this.lifetime.dispose();
    this.scanner?.dispose();
    this.toolGeneration += 1;
  }
}

export class ProfileView implements View {
  public readonly element = element("div", "page page--narrow");
  private readonly lifetime = new Lifetime();
  public constructor(
    auth: BrowserAuthManager,
    api: CanvassApi,
    selectOrganization: (id: string) => Promise<void>,
    signOut: () => Promise<void>,
  ) {
    const account = auth.account!;
    const error = errorMessage();
    const email = field("New email", "new_email", "email", "email");
    const emailPassword = field(
      "Current password",
      "email_password",
      "password",
      "current-password",
    );
    const emailNotice = notice();
    const emailForm = element(
      "form",
      "stack",
      heading(2, "Change email"),
      email.label,
      emailPassword.label,
      emailNotice,
    );
    const emailSubmit = element("button", "button", "Send verification email");
    emailSubmit.type = "submit";
    emailForm.append(emailSubmit);
    emailForm.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!emailForm.reportValidity() || emailSubmit.disabled) return;
      emailSubmit.disabled = true;
      error.textContent = "";
      void api
        .changeEmail(
          email.input.value.trim(),
          emailPassword.input.value,
          this.lifetime.abort.signal,
        )
        .then(() => {
          if (this.lifetime.active)
            emailNotice.textContent =
              "Check the new address for a verification link. Your current email remains active until you confirm it.";
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        })
        .finally(() => {
          emailPassword.input.value = "";
          emailSubmit.disabled = false;
        });
    });
    const current = field(
      "Current password",
      "current_password",
      "password",
      "current-password",
    );
    const next = field(
      "New password (at least 15 characters)",
      "new_password",
      "password",
      "new-password",
    );
    next.input.minLength = 15;
    const confirm = field("Confirm password", "confirmation", "password", "new-password");
    const passwordForm = element(
      "form",
      "stack",
      heading(2, "Change password"),
      current.label,
      next.label,
      confirm.label,
    );
    const passwordSubmit = element("button", "button", "Change password and sign out");
    passwordSubmit.type = "submit";
    passwordForm.append(passwordSubmit);
    passwordForm.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!passwordForm.reportValidity() || passwordSubmit.disabled) return;
      if (next.input.value !== confirm.input.value) {
        error.textContent = "The passwords do not match.";
        return;
      }
      passwordSubmit.disabled = true;
      error.textContent = "";
      void api
        .changePassword(current.input.value, next.input.value, this.lifetime.abort.signal)
        .then(async () => {
          if (this.lifetime.active) await signOut();
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        })
        .finally(() => {
          current.input.value = next.input.value = confirm.input.value = "";
          passwordSubmit.disabled = false;
        });
    });
    this.element.append(
      heading(1, "Profile"),
      panel(
        heading(2, account.user.displayName),
        element("p", null, account.user.email),
        element("p", null, `${account.organization.name} · ${roleLabels[account.role]}`),
      ),
      error,
    );
    if (account.role === "superadmin") {
      const choices = panel(heading(2, "Organisation"), notice("Loading organisations…"));
      this.element.append(choices);
      void auth
        .organizations()
        .then((organizations) => {
          if (!this.lifetime.active) return;
          const choice = selectField(
            "Act in organisation",
            organizations.map(({ id, name }) => [id, name]),
            account.organization.id,
          );
          choices.replaceChildren(
            heading(2, "Organisation"),
            choice.label,
            runButton(
              "Select organisation",
              () => selectOrganization(choice.select.value),
              error,
            ),
          );
        })
        .catch((failure: unknown) => {
          if (this.lifetime.active) error.textContent = message(failure);
        });
    }
    this.element.append(
      panel(emailForm),
      panel(passwordForm),
      runButton("Sign out", signOut, error, "button button--danger"),
    );
  }
  public dispose(): void {
    this.lifetime.dispose();
  }
}

export class InboxView implements View {
  public readonly element = element("div", "page page--narrow");
  private readonly lifetime = new Lifetime();
  private items: readonly Notification[] = [];
  private cursor: string | null = null;
  private generation = 0;
  private busy = false;
  private selected: Notification | null = null;
  private readonly content = element("div", "stack");
  private readonly error = errorMessage();
  public constructor(
    private readonly api: CanvassApi,
    private readonly onRead: () => void,
  ) {
    this.element.append(
      heading(1, "Messages"),
      notice("Messages from your branch. Web notifications are available here in your inbox."),
      this.error,
      actions(runButton("Refresh", () => this.load(false), this.error)),
      this.content,
    );
    void this.load(false);
  }
  private async load(older: boolean): Promise<void> {
    if (older && this.busy) return;
    const generation = ++this.generation;
    this.busy = true;
    this.error.textContent = "";
    try {
      const page = await this.api.notifications(
        older ? this.cursor : null,
        this.lifetime.abort.signal,
      );
      if (!this.lifetime.active || generation !== this.generation) return;
      this.items = older
        ? [...new Map([...this.items, ...page.items].map((item) => [item.id, item])).values()]
        : page.items;
      this.cursor = page.next_cursor;
      this.selected = null;
      this.render();
      this.onRead();
    } catch (failure) {
      if (this.lifetime.active && generation === this.generation)
        this.error.textContent = message(failure);
    } finally {
      if (generation === this.generation) this.busy = false;
    }
  }
  private async open(id: string): Promise<void> {
    const generation = ++this.generation;
    try {
      const notification = await this.api.readNotification(id, this.lifetime.abort.signal);
      if (!this.lifetime.active || generation !== this.generation) return;
      this.items = this.items.map((item) => (item.id === id ? notification : item));
      this.selected = notification;
      this.onRead();
      this.render();
    } catch (failure) {
      if (this.lifetime.active && generation === this.generation)
        this.error.textContent = message(failure);
    }
  }
  private render(): void {
    if (this.selected) {
      const item = this.selected;
      this.content.replaceChildren(
        button("← Back to messages", "button button--text", () => {
          this.selected = null;
          this.render();
        }),
        panel(
          heading(2, item.title),
          element("p", "muted", `${item.branch_name} · ${dateLabel(item.created_at)}`),
          element("div", "message-body", item.body),
        ),
      );
      return;
    }
    this.content.replaceChildren(
      ...this.items.map((item) =>
        button(
          `${item.read_at === null ? "● " : ""}${item.title}\n${item.branch_name} · ${dateLabel(item.created_at)}`,
          `message-row${item.read_at === null ? " message-row--unread" : ""}`,
          () => this.open(item.id),
        ),
      ),
    );
    if (this.items.length === 0) this.content.append(notice("No messages yet."));
    if (this.cursor)
      this.content.append(runButton("Older messages", () => this.load(true), this.error));
  }
  public dispose(): void {
    this.lifetime.dispose();
    this.items = [];
    this.selected = null;
  }
}
